import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { FileText, Download, Filter, BarChart3 } from 'lucide-react';

const REPORT_DESCRIPTIONS = {
  group_sessions: 'All group sessions with participant/staff counts, status, and ratio validation',
  staff_allocation: 'Staff assignments across group sessions with roles and attendance',
  participant_attendance: 'Participant attendance records for published/active sessions',
  service_delivery: 'Service delivery links with review status and exception flags',
  exceptions: 'Service links with exception flags requiring manual review',
};

export default function ReportsPage() {
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedReport, setSelectedReport] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ start_date: '', end_date: '', state_id: '', branch_id: '', status: '' });

  useEffect(() => {
    api.get('/reports/types').then(res => setReportTypes(res.data.data || [])).catch(() => {});
  }, []);

  const generateReport = async () => {
    if (!selectedReport) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/reports/${selectedReport}?${params.toString()}`);
      setReport(res.data.data);
      toast.success(`Report generated: ${res.data.data.total} rows`);
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to generate report');
    } finally { setLoading(false); }
  };

  const downloadCSV = async () => {
    if (!selectedReport) return;
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/reports/${selectedReport}/csv?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedReport}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Failed to download CSV');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><BarChart3 className="h-6 w-6" /> Reports & Downloads</h1>
        <p className="text-muted-foreground mt-1">Generate and export operational and compliance reports</p>
      </div>

      {/* Report Selection + Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Filter className="h-4 w-4" /> Report Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Report Type</Label>
            <Select value={selectedReport} onValueChange={v => { setSelectedReport(v); setReport(null); }}>
              <SelectTrigger data-testid="report-type-select"><SelectValue placeholder="Select a report..." /></SelectTrigger>
              <SelectContent>
                {reportTypes.map(r => (
                  <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedReport && <p className="text-xs text-muted-foreground mt-1">{REPORT_DESCRIPTIONS[selectedReport]}</p>}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={filters.start_date} onChange={e => setFilters({...filters, start_date: e.target.value})} data-testid="filter-start-date" />
            </div>
            <div>
              <Label className="text-xs">End Date</Label>
              <Input type="date" value={filters.end_date} onChange={e => setFilters({...filters, end_date: e.target.value})} data-testid="filter-end-date" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status} onValueChange={v => setFilters({...filters, status: v})}>
                <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_statuses">All</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={generateReport} disabled={!selectedReport || loading} data-testid="generate-report-btn">
                <FileText className="h-4 w-4 mr-2" /> {loading ? 'Generating...' : 'Generate'}
              </Button>
              {report && (
                <Button variant="outline" onClick={downloadCSV} data-testid="download-csv-btn">
                  <Download className="h-4 w-4 mr-2" /> CSV
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {report && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">{report.title}</CardTitle>
                <CardDescription>{report.total} rows | Generated {new Date(report.generated_at).toLocaleString()}</CardDescription>
              </div>
              <Badge variant="outline">{report.total} results</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {report.rows.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No data found for the selected filters</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {report.headers.map(h => (
                        <TableHead key={h} className="text-xs whitespace-nowrap">{h.replace(/_/g, ' ')}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.rows.slice(0, 100).map((row, i) => (
                      <TableRow key={i}>
                        {report.headers.map(h => (
                          <TableCell key={h} className="text-xs max-w-[200px] truncate">{String(row[h] ?? '')}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {report.rows.length > 100 && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">Showing 100 of {report.total} rows. Download CSV for full data.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
